import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPortalHub,
  subscribeToClinic,
  // unsubscribeFromClinic,
} from "../services/SignalR";
import {
  HttpTransportType,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { isProduction } from "../utils/envHelper";
import { webServiceUrl } from "../utils/servicesHelper";
import { useAuth0 } from "@auth0/auth0-react";

export const useSignalR = (
  clinicId,
  handleDeviceListUpdated,
  setIsLoadingSignalR
) => {
  const { getAccessTokenSilently } = useAuth0();
  const [connection, setConnection] = useState(null);
  const isSubscribed = useRef(false);
  const reconnectedRef = useRef(true);

  // connection to signalR
  useEffect(() => {
    const signalRConnection = async () => {
      try {
        const hubName = await getPortalHub();
        const newConnection = new HubConnectionBuilder()
          .configureLogging(isProduction ? LogLevel.None : LogLevel.Debug)
          .withUrl(`${webServiceUrl}/${hubName?.properties?.hub}`, {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
          })
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
              return (Math.random() + 0.2) * 10000;
            },
          })
          .build();

        setConnection(newConnection);
      } catch (e) {
        //console.log(e);
      }
    };
    signalRConnection();
  }, [setConnection]);

  const onSignalRConnected = useCallback(async () => {
    if (connection && connection.state === HubConnectionState.Connected) {
      try {
        //subscribe to clinic
        const token = await getAccessTokenSilently();
        const response = await subscribeToClinic(token, clinicId, connection);

        const data = JSON.parse(response);
        if (data?.state === true) {
          console.log("handle before list updated");
          connection?.off("DeviceListUpdated");
          connection.on("DeviceListUpdated", function (data) {
            isSubscribed.current = true;
            handleDeviceListUpdated(data);
          });
        }
      } catch (e) {
        //console.log("Could not subscribe to clinic.");
      }
    }
  }, [clinicId, connection, getAccessTokenSilently, handleDeviceListUpdated]);

  // establish the connection
  useEffect(() => {
    if (connection && connection?.state === HubConnectionState.Disconnected) {
      connection
        .start()
        .then((result) => {
          console.log("Connected!");
          if (!isSubscribed.current) onSignalRConnected();

          connection.onreconnecting(() => {
            console.log("Reconnecting...");
            // Show loading spinner during reconnection
            reconnectedRef.current = false;
            setIsLoadingSignalR(true);
          });

          connection.onreconnected(async (connectionId) => {
            // Hide loading spinner if connection closes
            setIsLoadingSignalR(false);
            console.log(
              `Reconnected. Connection Id: ${connectionId} or ${connection?.connectionId}`
            );
            // console.log(connection);
            await onSignalRConnected();
            reconnectedRef.current = true;
          });
        })
        .catch((e) => {
          console.error("Connection failed: ", e);
        });

      connection.onclose((e) => {
        if (e) {
          console.error(`Connection closed with error: ${e}`);
          // Try to restart the connection
          if (connection.state === HubConnectionState.Disconnected) {
            // Set loading state to true when attempting to reconnect
            setIsLoadingSignalR(true);
            connection
              .start()
              .then(() => {
                onSignalRConnected();
                // Reset loading state when reconnected successfully
                setIsLoadingSignalR(false);
              })
              .catch((e) => {
                console.error("Connection start failed after disconnect: ", e);
                // Reset loading state if reconnection fails
                setIsLoadingSignalR(false);
              });
          }
        } else {
          console.warn("Connection closed.");
        }
      });
    }

    return () => {
      if (connection) {
        console.warn("connection is gonna stop");
        connection.stop();
      }
    };
  }, [
    clinicId,
    connection,
    getAccessTokenSilently,
    onSignalRConnected,
    setIsLoadingSignalR,
  ]);

  return { connection, reconnectedRef };
};
